// React
import { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";

// External libraries
import useResizeObserver from "use-resize-observer";

function getBaseOverflowContext(size) {
    if (size < 1) return [];
    return new Array(size).fill(null).map(() => ({ overflow: false }));
}

export function useOverflowObserver(contextSize, initializationOptions = {}) {
    const [overflowContext, setOverflowContext] = useState([]);

    const measuringRef = useRef(false);
    const containerRef = useRef(null);

    const initialize = useCallback(() => {
        measuringRef.current = true;
        setOverflowContext(getBaseOverflowContext(contextSize));
    }, [contextSize]);

    useEffect(() => {
        if (!contextSize) return;
        initialize();
    }, [contextSize]);

    useLayoutEffect(() => {
        if (!measuringRef.current) return;

        const container = containerRef.current;
        if (!container || initializationOptions.Skip) {
            measuringRef.current = false;
            return;
        }

        const isOverflowing = container.scrollWidth > container.clientWidth;
        if (!isOverflowing) {
            measuringRef.current = false;
            return;
        }

        const nextIndex = overflowContext.findIndex(item => !item.overflow);
        if (nextIndex === -1) {
            measuringRef.current = false; 
            return;
        }

        setOverflowContext(prev =>
            prev.map((item, i) => (i === nextIndex ? { overflow: true } : item))
        );
    }, [overflowContext]);

    useResizeObserver({
        ref: containerRef,
        onResize: initialize
    });

    const getOverflow = useCallback((index) => {
        return overflowContext[index]?.overflow;
    }, [overflowContext]);

    return {
        overflowContext,
        containerRef,
        getOverflow,
        initialize
    };
}